import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

const DROP_DENSITY = 0.00034; // drops per px²
const WIND = -0.16; // radians, slight lean to the left

function makeDrop(w, h, randomY = true) {
  const depth = Math.random(); // 0 = far, 1 = near
  return {
    x: Math.random() * (w + 200) - 100,
    y: randomY ? Math.random() * h : -40,
    len: 12 + depth * 26,
    speed: 700 + depth * 900,
    width: 0.6 + depth * 0.9,
    alpha: 0.18 + depth * 0.42,
    drift: Math.tan(WIND),
  };
}

const DEFAULT_EVERY = [6000, 14000];

function useCanvasSize(ref, onResize) {
  const cb = useRef(onResize);
  cb.current = onResize;
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cb.current?.(canvas.clientWidth, canvas.clientHeight);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    return () => ro.disconnect();
  }, [ref]);
}

// Canvas rain: individually simulated streaks, dpr-aware, paused when off-screen or hidden.
export function RainCanvas({ intensity = 1 }) {
  const ref = useRef(null);
  const drops = useRef([]);

  useCanvasSize(ref, (w, h) => {
    const count = Math.round(w * h * DROP_DENSITY * intensity);
    drops.current = Array.from({ length: count }, () => makeDrop(w, h));
  });

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let last = 0;
    let visible = true;

    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      if (!visible || document.hidden) { last = t; return; }
      const dt = Math.min((t - last) / 1000, 0.05);
      last = t;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      ctx.lineCap = "round";
      for (const d of drops.current) {
        d.y += d.speed * dt;
        d.x += d.speed * d.drift * dt;
        if (d.y > h + 40) Object.assign(d, makeDrop(w, h, false));
        ctx.strokeStyle = `rgba(205, 222, 255, ${d.alpha})`;
        ctx.lineWidth = d.width;
        ctx.beginPath();
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x + d.len * d.drift, d.y - d.len);
        ctx.stroke();
      }
    };

    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(canvas);
    raf = requestAnimationFrame((t) => { last = t; frame(t); });
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen" data-testid="hero-rain" aria-hidden />;
}

// Jagged main channel with a few thinner branches.
function makeBolt(w, h) {
  const x0 = w * (0.12 + Math.random() * 0.76);
  const endY = h * (0.42 + Math.random() * 0.28);
  const main = [[x0, -20]];
  const branches = [];
  let x = x0;
  let y = -20;
  while (y < endY) {
    x += (Math.random() - 0.5) * 80;
    y += 14 + Math.random() * 26;
    main.push([x, y]);
    if (Math.random() < 0.28 && y < endY * 0.85) {
      const dir = Math.random() < 0.5 ? -1 : 1;
      const br = [[x, y]];
      let bx = x;
      let by = y;
      const n = 3 + Math.floor(Math.random() * 5);
      for (let i = 0; i < n; i++) {
        bx += dir * (14 + Math.random() * 30);
        by += 10 + Math.random() * 24;
        br.push([bx, by]);
      }
      branches.push(br);
    }
  }
  return { x0, main, branches, w, h };
}

function strokePath(ctx, pts, width, style, blur, blurColor) {
  ctx.lineWidth = width;
  ctx.strokeStyle = style;
  ctx.shadowBlur = blur;
  ctx.shadowColor = blurColor;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.stroke();
}

// Flicker envelope: strike, dim, re-strike, slow fade.
function envelope(t) {
  if (t < 0.06) return t / 0.06;
  if (t < 0.14) return 1 - ((t - 0.06) / 0.08) * 0.8;
  if (t < 0.2) return 0.2 + ((t - 0.14) / 0.06) * 0.8;
  return Math.max(0, 1 - (t - 0.2) / 0.8) ** 1.6;
}

function drawBolt(ctx, bolt, a) {
  const { x0, main, branches, w, h } = bolt;
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = "lighter";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const sky = ctx.createRadialGradient(x0, 0, 0, x0, 0, Math.max(w, h) * 0.65);
  sky.addColorStop(0, `rgba(200, 222, 255, ${0.8 * a})`);
  sky.addColorStop(0.4, `rgba(130, 172, 255, ${0.32 * a})`);
  sky.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = `rgba(180, 205, 255, ${0.14 * a})`;
  ctx.fillRect(0, 0, w, h);

  for (const br of branches) {
    strokePath(ctx, br, 9, `rgba(140, 180, 255, ${0.24 * a})`, 24, `rgba(120,170,255,${a})`);
    strokePath(ctx, br, 1.8, `rgba(240, 246, 255, ${0.95 * a})`, 0, "transparent");
  }
  strokePath(ctx, main, 22, `rgba(120, 170, 255, ${0.3 * a})`, 60, `rgba(120,170,255,${a})`);
  strokePath(ctx, main, 8, `rgba(170, 200, 255, ${0.55 * a})`, 22, `rgba(170,200,255,${a})`);
  strokePath(ctx, main, 3.2, `rgba(255, 255, 255, ${a})`, 0, "transparent");

  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = "source-over";
}

// Real lightning bolts drawn on canvas; a strike lands every `every` ms range while active.
export function LightningCanvas({ active = true, every = DEFAULT_EVERY }) {
  const ref = useRef(null);
  const [strikes, setStrikes] = useState(0);
  const bolt = useRef(null);
  const startedAt = useRef(0);
  const raf = useRef(0);

  useCanvasSize(ref);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let timer;
    const DURATION = 1250;

    const animate = (t) => {
      const p = (t - startedAt.current) / DURATION;
      if (p >= 1 || !bolt.current) {
        ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
        bolt.current = null;
        return;
      }
      drawBolt(ctx, bolt.current, envelope(p));
      raf.current = requestAnimationFrame(animate);
    };

    const strike = () => {
      if (!document.hidden) {
        bolt.current = makeBolt(canvas.clientWidth, canvas.clientHeight);
        startedAt.current = performance.now();
        cancelAnimationFrame(raf.current);
        raf.current = requestAnimationFrame(animate);
        setStrikes((s) => s + 1);
      }
      timer = setTimeout(strike, every[0] + Math.random() * (every[1] - every[0]));
    };
    timer = setTimeout(strike, 1200 + Math.random() * 2500);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf.current);
      bolt.current = null;
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    };
  }, [active, every]);

  return (
    <canvas
      ref={ref}
      className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen"
      data-testid="hero-lightning"
      data-strikes={strikes}
      aria-hidden
    />
  );
}

export default function StormLayers({ active = true, intensity = 1, lightningEvery }) {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <>
      <div className="fog-layer" aria-hidden />
      <RainCanvas intensity={intensity} />
      <LightningCanvas active={active} every={lightningEvery || DEFAULT_EVERY} />
    </>
  );
}
